import type { MouseEvent } from "react";

import dayjs from "dayjs";

import { shortMonthAndYearWithDelimiter } from "@similarweb/pro-logic-components";

import {
    ChangePercentage,
    DefaultCellRightAlign,
    LeadingSite,
    OrganicPaid,
    SearchKeywordCell,
    TrafficShareWithVisits,
} from "app/components/React/Table/cells";
import { IndexCell } from "app/components/React/Table/cells/IndexCell";
import { IntentCell } from "app/components/React/Table/cells/IntentCell";
import { KeywordDifficultyCell } from "app/components/React/Table/cells/KeywordDifficultyCell";
import { PercentageCellRightAlign } from "app/components/React/Table/cells/PercentageCell";
import { RowSelectionConsumer } from "app/components/React/Table/cells/RowSelection";
import { DefaultCellHeader, DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { SelectAllRowsHeaderCellConsumer } from "app/components/React/Table/headerCells/SelectAllRowsHeaderCell";
import {
    NO_DATA_SIGN,
    THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE,
    THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
} from "app/filters/const";
import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
import DurationService from "app/services/DurationService";
import { allTrackers } from "app/services/track/track";
import CoreNumberCell from "pro-features/components/core cells/src/CoreNumberCell/CoreNumberCell";
import CoreTrendsBarCell from "pro-features/components/core cells/src/CoreTrendsBarCell/CoreTrendsBarCell";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { ActivationSetCellButton } from "../enriched-row-components/activation-set-cell-enriched/ActivationSetCellButton";

import {
    ActivationSetLeaderCell,
    ActivationSetLeaderCellContent,
    ActivationSetLeaderCellDomain,
} from "./styledComponents";

export const ACITAVION_SET_LEADER_FIELD = "filteredLeadingDomainClicks";
const DEFAULT_SORT_FIELD = "volume";
const DEFAULT_SORT_DIRECTION = "desc";
const onItemClick = (keyword: string) => {
    allTrackers.dispatchTrackEvent("Internal Link", "click", `analyze keyword/${keyword}`);
};
export const MonitorKeywordsGroupTableSettings = {
    defaultSortField: DEFAULT_SORT_FIELD,
    defaultSortDirection: DEFAULT_SORT_DIRECTION,
    searchFieldName: "Keyword",
    getColumns: (
        duration: string,
        activationSetId,
        sortedColumn = { field: "volume", sortDirection: "desc" },
        keywordAdsState?: string,
        keywordOverviewState?: string,
    ) => {
        const durationObject = DurationService.getDurationData(duration);
        const columns: any[] = [
            {
                fixed: true,
                cellComponent: RowSelectionConsumer,
                headerComponent: SelectAllRowsHeaderCellConsumer,
                sortable: false,
                visible: true,
                width: 48,
            },
            {
                fixed: true,
                field: "#",
                cellComponent: IndexCell,
                disableHeaderCellHover: true,
                sortable: false,
                width: 40,
                headerComponent: DefaultCellHeader,
            },
            {
                field: "keyword",
                fixed: true,
                sortable: true,
                showTotalCount: true,
                cellComponent: (props) => {
                    const defaultProps = {
                        ...props,
                        ...(keywordOverviewState ? {} : { destinationState: null }),
                    };
                    return (
                        <SearchKeywordCell
                            {...defaultProps}
                            onItemClick={onItemClick}
                            adsUrl={
                                keywordAdsState
                                    ? proNavigator.href(keywordAdsState, {
                                          keyword: props.value,
                                      })
                                    : undefined
                            }
                            navigatorParams={null}
                        />
                    );
                },
                displayName: translate("workspaces.ppc.table.column.keyword"),
                tooltip: translate("workspaces.ppc.table.column.keyword.tooltip"),
                width: 160,
            },
            {
                field: "volume",
                sortable: true,
                displayName: translate("keywordresearch.monitorkeywords.ppc.table.column.avg.volume"),
                tooltip: translate("keywordresearch.monitorkeywords.ppc.table.column.avg.volume.tooltip"),
                format: "minAbbrNumber:50",
                headerComponent: DefaultCellHeaderRightAlign,
                cellComponent: DefaultCellRightAlign,
                width: 100,
            },
            {
                field: "change",
                sortable: true,
                cellComponent: ChangePercentage,
                displayName: translate("workspaces.ppc.table.column.change.month.over.month"),
                tooltip: translate("workspaces.ppc.table.column.change.tooltip"),
                width: 100,
            },
            {
                field: "clicksShareOrganic",
                sortable: true,
                cellComponent: OrganicPaid,
                displayName: translate("keywordresearch.monitorkeywords.ppc.table.column.organic_vs_paid"),
                tooltip: translate("keywordresearch.monitorkeywords.ppc.table.column.organic_vs_paid.tooltip"),
                width: 130,
            },
            activationSetId && {
                field: ACITAVION_SET_LEADER_FIELD,
                sortable: true,
                cellComponent: (props) => {
                    const {
                        value: { domain, clicks },
                    } = props as { value: { domain: string; clicks: string } };

                    const onClick = (e: MouseEvent) => {
                        e.stopPropagation();
                        allTrackers.dispatchTrackEvent("Open", "click", "Project Leader/Expand");
                        props.onCellClick(e, props.row?.index);
                    };

                    return domain ? (
                        <ActivationSetLeaderCell>
                            <ActivationSetCellButton
                                onClick={onClick}
                                className="enrich"
                            />
                            <ActivationSetLeaderCellDomain $isDomain={true}>{domain}</ActivationSetLeaderCellDomain>
                            <ActivationSetLeaderCellContent>{clicks}</ActivationSetLeaderCellContent>
                        </ActivationSetLeaderCell>
                    ) : (
                        <div>{NO_DATA_SIGN}</div>
                    );
                },
                displayName: translate("keywordresearch.monitorkeywords.ppc.table.column.activation_set_leader"),
                tooltip: translate("keywordresearch.monitorkeywords.ppc.table.column.activation_set_leader.tooltip"),
                width: 190,
            },
            {
                field: "volumeTrends",
                displayName: translate("workspaces.ppc.table.column.volume.trend"),
                tooltip: translate("workspaces.ppc.table.column.trend.tooltip"),
                cellComponent: ({ value = [] }) => {
                    const trends = value?.map(({ value: trendValue, key }: { value: number; key: string }) => ({
                        value: trendValue,
                        tooltip: (
                            <span>
                                <strong>{`${minAbbrNumberFilter()(
                                    trendValue,
                                    THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE,
                                )}`}</strong>
                                &nbsp;
                                {translate("monitor.keyword.list.page.table.column.volume_trend.cell.tooltip", {
                                    date: dayjs.utc(key).format(shortMonthAndYearWithDelimiter),
                                })}
                            </span>
                        ),
                    }));
                    return <CoreTrendsBarCell value={trends} />;
                },
                width: 130,
            },
            {
                field: "zeroClicksShare",
                sortable: true,
                displayName: translate("workspaces.ppc.table.column.zero.clicks.percentage"),
                tooltip: translate("workspaces.ppc.table.column.zero.clicks.tooltip"),
                cellComponent: PercentageCellRightAlign,
                width: 90,
            },

            {
                field: "intent",
                trackingName: "Keyword Intent",
                displayName: translate("keywords.table.header.intent.display.name"),
                tooltip: translate("keywords.table.header.intent.tooltip"),
                width: 120,
                cellComponent: IntentCell,
            },
            !activationSetId && {
                sortable: true,
                field: "leadingDomain",
                displayName: translate("workspaces.ppc.table.column.visits.leader"),
                tooltip: translate("workspaces.ppc.table.column.leadingdomain.toolip"),
                cellComponent: LeadingSite,
                width: 174,
            },
            {
                field: "cpc",
                sortable: true,
                displayName: translate("workspaces.ppc.table.column.cpc"),
                tooltip: translate("workspaces.ppc.table.column.cpc.tooltip"),
                cellComponent: ({ value }) => {
                    return (
                        <CoreNumberCell
                            value={value}
                            format="0.00"
                            prefix="$"
                        />
                    );
                },
                headerComponent: DefaultCellHeaderRightAlign,
                width: 80,
            },
            {
                field: "difficulty",
                trackingName: "Keyword Difficulty",
                displayName: translate("analysis.source.search.all.table.columns.keyword.difficulty.title"),
                tooltip: translate("analysis.source.search.all.table.columns.keyword.difficulty.tooltip", {
                    date: durationObject.raw.to.format("MMM YYYY"),
                }),
                width: 68,
                cellComponent: KeywordDifficultyCell,
                sortable: true,
                cellClass: "kw-difficulty-table-cell",
            },
        ];

        columns.splice(4, 0, {
            field: "totalClicksShare",
            sortable: true,
            trackingName: "Clicks Share",
            displayName: translate("keywordresearch.monitorkeywords.ppc.table.column.total.clicks"),
            tooltip: translate("keywordresearch.monitorkeywords.ppc.table.column.total.clicks.tooltip"),
            cellComponent: (props) => (
                <TrafficShareWithVisits
                    {...props}
                    totalVisitsRowProp="totalClicks"
                    thresholdTrafficShareWithVisitsPercentage={THRESHOLD_FOR_KEYWORD_RESEARCH_PERCENTAGE}
                    thresholdForTotalFilter={THRESHOLD_FOR_KEYWORD_RESEARCH_VALUE}
                    displayLessThanThreshold={true}
                    applyAbbrNumberFilter={true}
                />
            ),
            minWidth: 150,
            isResizable: false,
        });

        return columns.filter(Boolean).map((col: any) => {
            const isSorted = sortedColumn && col.field === sortedColumn.field;
            return {
                visible: true,
                headerComponent: DefaultCellHeader,
                isSorted,
                sortDirection: isSorted ? sortedColumn.sortDirection : DEFAULT_SORT_DIRECTION,
                ...col,
            };
        });
    },
};
